/**
 * DonutPieChart - a part-to-whole chart with donut and pie variants.
 *
 * Built on recharts. Accepts arbitrary row shapes through configurable keys,
 * normalizes anything unplottable out of the way, and always resolves to a
 * visible state (loading, error, empty, or chart) so it can be dropped straight
 * onto a dashboard without guard code around it.
 *
 * Usage:
 *   <DonutPieChart data={rows} nameKey="status" valueKey="count" />
 *   <DonutPieChart variant="pie" data={rows} title="Findings by severity" />
 */

import { useCallback, useMemo, useState, type ReactNode } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Sector, Tooltip } from "recharts";
import { cn } from "../../ui/utils";
import {
  CHART_PALETTE_DARK,
  CHART_PALETTE_LIGHT,
  OTHER_COLOR_DARK,
  OTHER_COLOR_LIGHT,
  BAR_FILL,
  MARK_STROKE_WIDTH,
  SEGMENT_PADDING_ANGLE,
  markGlow,
  formatCompact,
  formatNumber,
  formatShare,
  resolveErrorMessage,
  resolvePlotHeight,
  useElementWidth,
  useIsDarkTheme,
  usePrefersReducedMotion,
} from "../chartUtils";
// The elbow leader label and its collision handling are shared with the GRC dashboard donut and the
// AI companion's chat donut, so all three space a crowded tail the same way.
import {
  fitLeaderLabelPlot,
  leaderPlotIdealWidth,
  makeLeaderLabelRenderer,
} from "../../charts/donutCore";

/**
 * Recharts' own `<Pie>` defaults, which this chart does not override. The leader-label resolver has
 * to work from the same sweep recharts draws, so they are named here rather than repeated as bare
 * numbers at the call site.
 */
const RECHARTS_DEFAULT_START_ANGLE = 0;
const RECHARTS_DEFAULT_END_ANGLE = 360;

/** The `<Pie>`'s own `outerRadius`, as a fraction. Named because the label fitting solves against it. */
const OUTER_RADIUS_RATIO = 0.86;

/**
 * Width cap on the plot box, so a ring does not stretch the full width of a wide card and leave its
 * legend stranded. Leader labels raise this — see `maxPlotWidth` below.
 */
const PLOT_MAX_WIDTH = 280;

/** Share of the container the plot takes when the legend sits beside it. */
const SIDE_BY_SIDE_PLOT_SHARE = 0.48;
import {
  ChartEmptyState,
  ChartErrorBoundary,
  ChartErrorState,
  ChartLoadingState,
  ChartWarnings,
} from "../ChartStates";
import { SliceLegend, SliceTable, SliceTooltip } from "./DonutChrome";
import { normalizeChartData, type ChartSlice, type SliceSort } from "./donutUtils";

export interface DonutPieChartProps {
  /**
   * DOM id for the chart's root element. Stays on the same element across the
   * loading, error, empty, and plotted states, so anything anchored to it does
   * not lose its target when the state changes. Also seeds the ids of the
   * internal title and plot nodes.
   */
  id?: string;
  /**
   * Identifier for the chart instance, used for tests, analytics, and event
   * handlers. Rendered as `data-name`, since `name` is not a valid attribute on
   * a div, and passed back as the third argument to onSliceClick.
   */
  name?: string;

  /** Rows to plot. Null, undefined, and non-arrays resolve to the empty state. */
  data: unknown;
  /** Donut leaves a hole for the centre total; pie is solid. */
  variant?: "donut" | "pie";

  /** Field holding each row's category label. */
  nameKey?: string;
  /** Field holding each row's numeric value. */
  valueKey?: string;
  /** Optional field holding a per-row colour override. */
  colorKey?: string;

  title?: string;
  subtitle?: string;

  /** Plot height in px. The chart stays square and centres inside it. */
  height?: number;
  /** Replaces the built-in categorical hues. Assigned in order, never cycled. */
  palette?: readonly string[];

  loading?: boolean;
  /** Fetch failure. Takes precedence over every other state. */
  error?: string | Error | null;
  /** Retry handler shown on the error state. */
  onRetry?: () => void;
  emptyMessage?: string;

  /** Slices past this cap fold into a single neutral bucket. */
  maxSlices?: number;
  otherLabel?: string;
  sort?: SliceSort;

  showLegend?: boolean;
  legendPosition?: "top" | "right" | "bottom";
  /** Allow clicking legend items to hide and restore slices. */
  legendToggle?: boolean;
  /**
   * Draw each legend row's value, and its share, beside the label. BOTH default
   * off: the legend's job is to say which colour means what, and the numbers
   * are already on the chart — on the slices when `showSliceLabels` is set, and
   * in the tooltip regardless. Repeating them turns a key into a cramped table
   * and is what pushed the rows out of alignment on a narrow card.
   */
  showLegendValue?: boolean;
  showLegendShare?: boolean;

  /** Percentage labels drawn on the slices. Defaults on for pie, off for donut. */
  showSliceLabels?: boolean;
  /** Slices below this share stay unlabelled so text never collides. Ignored by `"leader"`. */
  minLabelShare?: number;
  /**
   * How a slice's percentage is drawn.
   *
   * `"inside"` (default) writes it on the slice and drops anything under {@link minLabelShare},
   * because there is no room inside a sliver — small categories simply go unlabelled.
   *
   * `"leader"` runs an elbow line out to the label instead, so every slice keeps its number however
   * thin it is. Labels that would land on top of each other are pushed apart by the shared
   * resolver, which is what makes a long tail of sub-1% slices readable rather than a smear. Costs
   * room outside the arc — the plot reserves a margin for it — so it wants a chart with a bit of
   * space around it.
   */
  labelStyle?: "inside" | "leader";

  showTooltip?: boolean;
  /** Expandable text equivalent of the chart. */
  showDataTable?: boolean;
  /** Surface notes about dropped, merged, or folded rows. */
  showWarnings?: boolean;

  /** Donut centre. Ignored by the pie variant. */
  showCenterTotal?: boolean;
  centerLabel?: string;
  /** Replaces the computed centre figure. */
  centerValue?: ReactNode;

  /** Suffix appended to every rendered value, for example "%" or " hrs". */
  unit?: string;
  valueFormatter?: (value: number) => string;

  onSliceClick?: (slice: ChartSlice, index: number, name?: string) => void;

  className?: string;
  ariaLabel?: string;
}


function renderActiveShape(props: any) {
  const {
    cx,
    cy,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    fill,
    stroke,
    strokeWidth,
    cornerRadius,
    paddingAngle,
  } = props ?? {};
  if (!Number.isFinite(outerRadius)) return null;
  return (
    <Sector
      cx={cx}
      cy={cy}
      innerRadius={innerRadius}
      outerRadius={outerRadius + 5}
      startAngle={startAngle}
      endAngle={endAngle}
      cornerRadius={cornerRadius}
      paddingAngle={paddingAngle}
      fill={fill}
      // The slice is outline-only, so its fill is transparent. Carry the
      // stroke through or the hovered arc grows into something invisible.
      stroke={stroke}
      strokeWidth={strokeWidth}
      style={stroke ? { filter: markGlow(stroke) } : undefined}
    />
  );
}

interface SliceLabelProps {
  cx: number;
  cy: number;
  midAngle: number;
  innerRadius: number;
  outerRadius: number;
  percent: number;
  minShare: number;
}

/**
 * Percentage drawn at the slice mid-angle. White on the fill is the one place
 * text may sit on a data colour; a shadow keeps it legible on light hues.
 * Slivers are skipped so labels never overlap or spill past their slice.
 */
function renderSliceLabel(props: SliceLabelProps) {
  const { cx, cy, midAngle, innerRadius, outerRadius, percent, minShare } = props ?? {};
  if (!Number.isFinite(percent) || percent < minShare) return null;
  if (!Number.isFinite(cx) || !Number.isFinite(cy) || !Number.isFinite(midAngle)) return null;

  const RAD = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.62;
  const x = cx + radius * Math.cos(-midAngle * RAD);
  const y = cy + radius * Math.sin(-midAngle * RAD);

  return (
    <text
      x={x}
      y={y}
      fill="#ffffff"
      fontSize={11}
      fontWeight={700}
      textAnchor="middle"
      dominantBaseline="central"
      style={{ textShadow: "0 1px 2px rgba(0,0,0,0.45)", pointerEvents: "none" }}
    >
      {Math.round(percent * 100)}%
    </text>
  );
}

function DonutPieChartInner({
  id,
  name,
  data,
  variant = "donut",
  nameKey = "name",
  valueKey = "value",
  colorKey = "color",
  title,
  subtitle,
  height = 260,
  palette,
  loading = false,
  error = null,
  onRetry,
  emptyMessage = "There are no records to chart for the current selection.",
  maxSlices = 6,
  otherLabel = "Other",
  sort = "desc",
  showLegend = true,
  legendPosition = "top",
  legendToggle = true,
  showLegendValue = false,
  showLegendShare = false,
  showSliceLabels,
  minLabelShare = 0.08,
  labelStyle = "inside",
  showTooltip = true,
  showDataTable = false,
  showWarnings = true,
  showCenterTotal = true,
  centerLabel = "Total",
  centerValue,
  unit = "",
  valueFormatter,
  onSliceClick,
  className,
  ariaLabel,
}: DonutPieChartProps) {
  const isDark = useIsDarkTheme();
  const reduceMotion = usePrefersReducedMotion();
  const [containerRef, containerWidth] = useElementWidth<HTMLDivElement>();

  const [activeId, setActiveId] = useState<string | null>(null);
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(() => new Set());
  const [tableOpen, setTableOpen] = useState(false);

  const isDonut = variant === "donut";
  const plotHeight = resolvePlotHeight(height, 260);
  const activePalette = useMemo(() => {
    const source = palette?.filter?.(Boolean);
    if (source?.length) return source;
    return isDark ? CHART_PALETTE_DARK : CHART_PALETTE_LIGHT;
  }, [palette, isDark]);

  const formatValue = useCallback(
    (value: number) => {
      if (typeof valueFormatter === "function") {
        try {
          return valueFormatter(value);
        } catch {
          // A throwing formatter must not take the chart down with it.
          return formatNumber(value);
        }
      }
      return formatNumber(value);
    },
    [valueFormatter],
  );

  const { slices, warnings } = useMemo(
    () =>
      normalizeChartData(data, {
        nameKey,
        valueKey,
        colorKey,
        palette: activePalette,
        otherColor: isDark ? OTHER_COLOR_DARK : OTHER_COLOR_LIGHT,
        maxSlices,
        otherLabel,
        sort,
      }),
    [data, nameKey, valueKey, colorKey, activePalette, isDark, maxSlices, otherLabel, sort],
  );

  // Hidden slices leave the plot but keep their assigned colour, so restoring
  // one does not repaint the survivors.
  const visibleSlices = useMemo(
    () => slices.filter((slice) => !hiddenIds.has(slice.id)),
    [slices, hiddenIds],
  );
  const visibleTotal = useMemo(
    () => visibleSlices.reduce((sum, slice) => sum + slice.value, 0),
    [visibleSlices],
  );

  const activeIndex = useMemo(
    () => visibleSlices.findIndex((slice) => slice.id === activeId),
    [visibleSlices, activeId],
  );
  const activeSlice = activeIndex >= 0 ? visibleSlices[activeIndex] : null;

  const handleToggle = useCallback(
    (id: string) => {
      if (!legendToggle) return;
      setHiddenIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
          return next;
        }
        // Refuse the toggle that would empty the chart.
        if (prev.size + 1 >= slices.length) return prev;
        next.add(id);
        return next;
      });
    },
    [legendToggle, slices.length],
  );

  const handleSliceClick = useCallback(
    (_: unknown, index: number) => {
      const slice = visibleSlices?.[index];
      // `name` rides along so one handler can serve several charts.
      if (slice && typeof onSliceClick === "function") onSliceClick(slice, index, name);
    },
    [visibleSlices, onSliceClick, name],
  );

  const description = useMemo(() => {
    if (ariaLabel) return ariaLabel;
    const kind = isDonut ? "Donut" : "Pie";
    const base = title ? `${kind} chart: ${title}.` : `${kind} chart.`;
    if (!visibleSlices.length) return `${base} No data.`;
    const parts = visibleSlices
      .map((slice) => `${slice.label} ${formatValue(slice.value)}${unit}, ${formatShare(slice.value, visibleTotal)}`)
      .join(". ");
    return `${base} Total ${formatValue(visibleTotal)}${unit}. ${parts}.`;
  }, [ariaLabel, isDonut, title, visibleSlices, visibleTotal, unit, formatValue]);

  const errorMessage = resolveErrorMessage(error);
  const labelsOn = showSliceLabels ?? !isDonut;
  // Below this the plot and a readable legend column cannot both fit, so the
  // legend drops underneath regardless of the requested position.
  const sideBySide = legendPosition === "right" && containerWidth >= 400;
  // Top puts the key above the ring, on the card's left edge, matching every
  // other chart. It shares the stacked legend layout; only the order differs.
  const legendOnTop = legendPosition === "top";
  // A ring gap on a lone slice would cut a notch out of a full circle.
  const paddingAngle = visibleSlices.length > 1 ? SEGMENT_PADDING_ANGLE : 0;
  const leaderLabels = labelsOn && labelStyle === "leader";
  // Recharts is not told a start/end angle here, so it uses its own defaults; the resolver has to
  // be given the same ones or its mid-angles land somewhere else entirely.
  const sliceLabel = leaderLabels
    ? makeLeaderLabelRenderer(
        visibleSlices.map((slice) => slice.value),
        RECHARTS_DEFAULT_START_ANGLE,
        RECHARTS_DEFAULT_END_ANGLE,
        paddingAngle,
      )
    : undefined;
  // Leader labels are drawn outside the arc, so without a margin they clip at the plot edge.
  //
  // The margin is worked out from the ring rather than the other way round: `fitLeaderLabelPlot`
  // returns the largest radius whose labels clear this box, and the margin that makes recharts
  // land on it. Reserving a flat share per side instead cost the ring twice over — it cropped the
  // text, which is drawn past the end of the leader line and so beyond any geometry-only reserve,
  // and it charged a wide card for horizontal room it already had spare.
  //
  // Leader labels also raise the box's width cap, since a cap that fits the ring but not its
  // labels is exactly what squeezes the radius on a card wider than it is tall.
  const maxPlotWidth = leaderLabels
    ? Math.max(PLOT_MAX_WIDTH, leaderPlotIdealWidth(plotHeight))
    : PLOT_MAX_WIDTH;
  const boxWidth = sideBySide ? containerWidth * SIDE_BY_SIDE_PLOT_SHARE : containerWidth;
  // Before the first measurement there is no width to fit against; assume a square box, which is
  // what the cap gives on any card at least as wide as it is tall.
  const plotWidth = Math.min(boxWidth || plotHeight, maxPlotWidth);
  const plotFit = leaderLabels
    ? fitLeaderLabelPlot(plotWidth, plotHeight, OUTER_RADIUS_RATIO)
    : null;
  const plotMargin = plotFit
    ? {
        top: plotFit.marginY,
        right: plotFit.marginX,
        bottom: plotFit.marginY,
        left: plotFit.marginX,
      }
    : undefined;

  const header = (title || subtitle) && (
    <div className="mb-3 min-w-0">
      {title && (
        <h3
          id={id ? `${id}-title` : undefined}
          className="break-words text-sm font-bold leading-snug text-foreground"
        >
          {title}
        </h3>
      )}
      {subtitle && (
        <p className="mt-0.5 break-words text-xs leading-snug text-muted-foreground">{subtitle}</p>
      )}
    </div>
  );

  /* Error and loading short-circuit the plot but keep the header in place, so
     the surrounding layout never jumps or blanks between states. */
  if (errorMessage) {
    return (
      <div
        id={id}
        data-name={name}
        ref={containerRef}
        className={cn("w-full min-w-0", className)}
      >
        {header}
        <ChartErrorState height={plotHeight} message={errorMessage} onRetry={onRetry} />
      </div>
    );
  }

  if (loading) {
    return (
      <div
        id={id}
        data-name={name}
        ref={containerRef}
        className={cn("w-full min-w-0", className)}
      >
        {header}
        <ChartLoadingState height={plotHeight} />
      </div>
    );
  }

  // No plottable rows, or every value is zero: a zero-total ring has no
  // geometry, so recharts would render an invisible chart.
  if (!visibleSlices.length || visibleTotal <= 0) {
    return (
      <div
        id={id}
        data-name={name}
        ref={containerRef}
        className={cn("w-full min-w-0", className)}
      >
        {header}
        <ChartEmptyState
          height={plotHeight}
          message={
            slices.length && visibleTotal <= 0
              ? "Every category in this set has a value of zero."
              : emptyMessage
          }
        />
        {showWarnings && <ChartWarnings warnings={warnings} />}
      </div>
    );
  }

  return (
    <div
      id={id}
      data-name={name}
      ref={containerRef}
      className={cn("w-full min-w-0", className)}
    >
      {header}

      <div
        className={cn(
          "flex min-w-0 items-center gap-3",
          sideBySide ? "flex-row" : "flex-col",
          legendOnTop && "flex-col-reverse",
        )}
      >
        <div
          className={cn(
            "relative w-full shrink-0",
            // Side by side, the plot takes a share of the container rather than
            // a fixed width, so the legend always keeps a readable column.
            sideBySide ? "w-[48%]" : "mx-auto",
          )}
          id={id ? `${id}-plot` : undefined}
          style={{ height: plotHeight, maxWidth: maxPlotWidth }}
          // The native mouseleave on the box is the reliable "cursor left"
          // signal: per-slice handlers get skipped on fast exits, across the
          // padding gaps, and in the corners, which leaves hover state stuck.
          onMouseLeave={() => setActiveId(null)}
          role="img"
          aria-label={description}
        >
          <ResponsiveContainer width="100%" height="100%">
            <PieChart onMouseLeave={() => setActiveId(null)} margin={plotMargin}>
              {showTooltip && (
                <Tooltip
                  content={
                    <SliceTooltip
                      total={visibleTotal}
                      unit={unit}
                      valueFormatter={formatValue}
                    />
                  }
                  wrapperStyle={{ outline: "none", zIndex: 20 }}
                  isAnimationActive={!reduceMotion}
                />
              )}
              <Pie
                data={visibleSlices}
                dataKey="value"
                nameKey="label"
                cx="50%"
                cy="50%"
                innerRadius={isDonut ? "60%" : 0}
                outerRadius={`${OUTER_RADIUS_RATIO * 100}%`}
                paddingAngle={paddingAngle}
                cornerRadius={isDonut ? 4 : 0}
                minAngle={2}
                // No stroke here. This used to carry a 2px surface-coloured
                // rule that widened the gap, but each Cell now sets its own
                // outline colour for the neon treatment and overrides it — so
                // the prop did nothing except imply a gap that was no longer
                // being drawn. `paddingAngle` alone owns the spacing.
                label={
                  labelsOn
                    ? (sliceLabel ??
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      ((props: any) => renderSliceLabel({ ...props, minShare: minLabelShare })))
                    : undefined
                }
                labelLine={false}
                activeIndex={activeIndex >= 0 ? activeIndex : undefined}
                activeShape={renderActiveShape}
                onMouseEnter={(_, index: number) => setActiveId(visibleSlices?.[index]?.id ?? null)}
                onClick={handleSliceClick}
                isAnimationActive={!reduceMotion}
                animationDuration={600}
                className={onSliceClick ? "cursor-pointer" : undefined}
              >
                {visibleSlices.map((slice) => (
                  <Cell
                    key={slice.id}
                    // Outline treatment, matching the bars: the arc is drawn as
                    // a hollow band edged in its own hue and lit by a glow of
                    // the same colour, rather than as a solid wedge.
                    fill={BAR_FILL}
                    stroke={slice.color}
                    strokeWidth={MARK_STROKE_WIDTH}
                    style={{
                      transition: "opacity 200ms ease",
                      opacity: activeId == null || activeId === slice.id ? 1 : 0.4,
                      outline: "none",
                      filter: markGlow(slice.color),
                    }}
                  />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>

          {isDonut && showCenterTotal && (
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
              <span className="break-words text-lg font-bold leading-none text-foreground">
                {centerValue ??
                  (activeSlice
                    ? `${formatCompact(activeSlice.value)}${unit}`
                    : `${formatCompact(visibleTotal)}${unit}`)}
              </span>
              <span className="mt-1 break-words text-[10px] font-medium leading-tight text-muted-foreground">
                {activeSlice
                  ? `${formatShare(activeSlice.value, visibleTotal)} of total`
                  : centerLabel}
              </span>
            </div>
          )}
        </div>

        {showLegend && visibleSlices.length > 0 && (
          <SliceLegend
            slices={slices}
            total={visibleTotal}
            unit={unit}
            activeId={activeId}
            hiddenIds={hiddenIds}
            position={sideBySide ? "right" : "bottom"}
            interactive={legendToggle}
            showValue={showLegendValue}
            showShare={showLegendShare}
            valueFormatter={formatValue}
            onActivate={setActiveId}
            onToggle={handleToggle}
          />
        )}
      </div>

      {showWarnings && <ChartWarnings warnings={warnings} />}

      {showDataTable && (
        <SliceTable
          slices={visibleSlices}
          total={visibleTotal}
          unit={unit}
          categoryHeader={title || "Category"}
          open={tableOpen}
          valueFormatter={formatValue}
          onToggle={() => setTableOpen((open) => !open)}
        />
      )}
    </div>
  );
}

/**
 * Public entry point. Wraps the chart in a local error boundary so a render
 * crash stays inside the chart box instead of reaching the app-level boundary
 * and blanking the page.
 */
export function DonutPieChart(props: DonutPieChartProps) {
  const plotHeight = resolvePlotHeight(props?.height, 260);

  return (
    <ChartErrorBoundary height={plotHeight} onRetry={props?.onRetry}>
      <DonutPieChartInner {...props} />
    </ChartErrorBoundary>
  );
}

export default DonutPieChart;
